import {
  QuestionnaireType,
  TestDetailMoreInfoProps,
  TestKitsListType,
  TestKitType,
} from "@/common/types";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  getTestsKits,
  getVisualizationData,
} from "@/redux/actions/testKitActions";
import { RootState } from "@/redux/reducers";
import { isUserAdmin, isUserDoctor } from "@/utils/common.util";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import TestHistory from "@/containers/test-detail/test-history";
import TestVisualization from "@/containers/test-detail/test-visualization";
import { getQuestionnaireDetailByTestKit } from "@/redux/actions/questionnaireAction";
import TestQuestionnaire from "./questionnaire";
import TestLogs from "./logs";

const TestDetailMoreInfo: React.FC<TestDetailMoreInfoProps> = ({
  testId,
  patientId,
  testStatus,
  questionnaireStatus,
  logs,
}) => {
  const [testHistory, setTestHistory] = useState<TestKitType[]>([]);
  const [visualizationAvailable, setVisualizationAvailable] =
    useState<boolean>(false);

  const testKits = useSelector((state: RootState) => state.testKit).testKits;
  const { questionnaireDetailByTestKit } = useSelector(
    (state: RootState) => state.questionnaire
  );
  const visualization = useSelector(
    (state: RootState) => state.testKit
  ).visualization;

  const dispatch: any = useDispatch();
  const params = useParams();

  const loadInfo = () => {
    if (patientId) {
      const filterState = {
        doctor: "",
        patient: patientId.toString(),
        status: "",
      };
      dispatch(getTestsKits(1, filterState));
    }

    const isVisualizationAvailable = [
      "ANALYSIS_READY",
      "REPORT_READY",
    ].includes(testStatus);
    if ((isUserAdmin() || isUserDoctor()) && isVisualizationAvailable) {
      dispatch(getVisualizationData(testId));
    }
    if (testId && questionnaireStatus) {
      dispatch(getQuestionnaireDetailByTestKit(Number(testId)));
    }
  };

  useEffect(() => {
    if (
      testKits &&
      (testKits as TestKitsListType)?.results &&
      params.id &&
      patientId
    ) {
      const filteredTestKits = (testKits as TestKitsListType)?.results
        .filter((test) => test.id !== Number(params.id))
        .slice(0, 5);
      setTestHistory(filteredTestKits);
    }
  }, [testKits]);

  useEffect(() => {
    loadInfo();
  }, [testId, patientId]);

  useEffect(() => {
    if (visualization) {
      const isDataPresent =
        (visualization?.metrics &&
          Object.keys(visualization?.metrics)?.length > 0) ||
        (visualization?.taxa && Object.keys(visualization?.taxa)?.length > 0);
      setVisualizationAvailable(isDataPresent);
    }
  }, [visualization]);

  return (
    <Accordion
      type="single"
      collapsible
      defaultValue={testHistory.length > 0 ? "history" : ""}
      className="w-full px-4"
    >
      <AccordionItem value="history">
        <AccordionTrigger>History</AccordionTrigger>
        <AccordionContent>
          {patientId && testHistory.length > 0 && (
            <TestHistory testKits={testHistory} />
          )}
          {testHistory.length === 0 && <p>No previous test history found</p>}
        </AccordionContent>
      </AccordionItem>

      {questionnaireStatus && (
        <AccordionItem value="questionnaire">
          <AccordionTrigger>Questionnaire</AccordionTrigger>
          <AccordionContent>
            {testId && (
              <TestQuestionnaire
                questionnaire={
                  questionnaireDetailByTestKit as QuestionnaireType
                }
              />
            )}
          </AccordionContent>
        </AccordionItem>
      )}

      {(isUserAdmin() || isUserDoctor()) && visualizationAvailable && (
        <AccordionItem value="visualization">
          <AccordionTrigger>Visualization</AccordionTrigger>
          <AccordionContent>
            {testId && <TestVisualization visualization={visualization} />}
          </AccordionContent>
        </AccordionItem>
      )}

      <AccordionItem value="logs">
        <AccordionTrigger>Logs</AccordionTrigger>
        <AccordionContent>
          {testId && logs && <TestLogs logs={logs} />}
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
};

export default TestDetailMoreInfo;
